import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../hooks/useLanguage';
function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    let rafId: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [target, duration, start]);
  return count;
}
function StatItem({
  icon,
  value,
  suffix,
  label,
  animate,
  delay
}: {
  icon: string;
  value: number;
  suffix: string;
  label: string;
  animate: boolean;
  delay: string;
}) {
  const count = useCountUp(value, 2000, animate);
  return <div className={`flex flex-col items-center md:items-start justify-center group reveal-hidden ${animate ? `animate-fade-up ${delay}` : ''}`} data-id="element-391">
      <div className="mb-4 p-0 rounded-full bg-transparent group-hover:bg-white/15 transition-colors" data-id="element-392">
        <img src={icon} alt={label} className="w-10 h-10" data-id="element-393" />
      </div>
      <h3 className="text-3xl md:text-4xl font-bold mb-1 text-white" dir="ltr" data-id="element-394">
        {count}
        {suffix}
      </h3>
      <p className="text-sm md:text-base text-white font-medium" data-id="element-395">{label}</p>
    </div>;
}
export function StatsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const {
    t,
    direction
  } = useLanguage();
  const stats = [{
    icon: 'https://cdn.magicpatterns.com/uploads/b1z9KUz157t8uvAsuMhbbL/clients.svg',
    value: 100,
    suffix: '+',
    label: t('stats.clients')
  }, {
    icon: 'https://cdn.magicpatterns.com/uploads/aMMC8HfTXK5PT9YJZ3juCy/countries.svg',
    value: 30,
    suffix: '+',
    label: t('stats.countries')
  }, {
    icon: 'https://cdn.magicpatterns.com/uploads/5BZTkasV2vnie4xoAK511C/experience.svg',
    value: 30,
    suffix: '+',
    label: t('stats.experience')
  }, {
    icon: 'https://cdn.magicpatterns.com/uploads/2gPVCGp8VsbvfSj7hCv3s8/employees.svg',
    value: 200,
    suffix: '+',
    label: t('stats.employees')
  }];
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} className="gradient-dark-horizontal py-16 md:py-20" dir={direction} data-id="element-396">
      <div className="container mx-auto px-6 md:px-12" data-id="element-397">
        <div className="grid grid-cols-2 md:flex md:justify-between gap-8 text-center md:text-start text-white" data-id="element-398">
          {stats.map((stat, index) => <StatItem key={index} icon={stat.icon} value={stat.value} suffix={stat.suffix} label={stat.label} animate={isVisible} delay={`delay-${(index + 1) * 100}`} data-id="element-399" />)}
        </div>
      </div>
    </section>;
}